import { useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi, ChatRequest, ChatResponse, ChatAction } from '@/api'
import { useChatStore } from '@/stores/chatStore'
import { useTimerStore } from '@/stores'
import { taskKeys } from './useTasks'
import { timelogKeys } from './useTimeLogs'

/**
 * Hook to send a chat message and handle the response
 */
export function useSendMessage() {
  const queryClient = useQueryClient()
  const { addMessage, setSessionId, setLoading, sessionId } = useChatStore()
  const { startTimer, stopTimer } = useTimerStore()

  return useMutation({
    mutationFn: async (message: string) => {
      // Add user message to store immediately
      addMessage({ role: 'user', content: message })
      setLoading(true)

      const request: ChatRequest = {
        message,
        session_id: sessionId || undefined,
        channel: 'pwa',
      }

      return chatApi.send(request)
    },
    onSuccess: (response: ChatResponse) => {
      // Add assistant response
      addMessage({
        role: 'assistant',
        content: response.response,
        actions: response.actions,
      })

      // Store session ID for conversation continuity
      if (response.session_id) {
        setSessionId(response.session_id)
      }

      // Handle actions from the response
      if (response.actions) {
        handleChatActions(response.actions, queryClient, startTimer, stopTimer)
      }

      setLoading(false)
    },
    onError: () => {
      addMessage({
        role: 'assistant',
        content: 'Hmm, I couldn\'t reach the server just now. Could you try that again?',
      })
      setLoading(false)
    },
  })
}

/**
 * Handle actions returned from chat responses
 */
function handleChatActions(
  actions: ChatAction[],
  queryClient: ReturnType<typeof useQueryClient>,
  startTimer: (taskId: string, taskTitle: string) => void,
  stopTimer: () => { taskId: string; elapsedSeconds: number } | null
) {
  for (const action of actions) {
    switch (action.type) {
      case 'task_created':
      case 'task_updated':
        // Invalidate task queries to reflect changes
        queryClient.invalidateQueries({ queryKey: taskKeys.all })
        break

      case 'timer_started':
        if (action.data?.task_id && action.data?.task_title) {
          startTimer(
            action.data.task_id as string,
            action.data.task_title as string
          )
        }
        break

      case 'timer_stopped':
        stopTimer()
        // Invalidate timelogs to reflect new entry
        queryClient.invalidateQueries({ queryKey: timelogKeys.all })
        break

      case 'client_created':
        // Could invalidate client queries when implemented
        break
    }
  }
}

/**
 * Hook to clear chat history and start a new session
 */
export function useClearChat() {
  const { clearMessages, setSessionId } = useChatStore()

  return () => {
    clearMessages()
    setSessionId(null)
  }
}
